import { CloudIcon, LaptopIcon, ShieldCheckIcon } from "lucide-react";
import type { CmmdOrgProviderPolicy, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";

import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { AutomationModelPickerField } from "./AutomationModelPickerField";
import { AutomationRunLocationChoice } from "./AutomationRunLocationChoice";
import type { AutomationTemplateDefinition } from "./automationTemplates";
import type {
  AutomationDraft,
  AutomationProjectOption,
  AutomationScheduleKind,
} from "./automationFormModel";
import type { ModelPickerProviderAvailability } from "~/components/chat/modelPickerProviderAvailability";

interface CreateAutomationEditorProps {
  readonly draft: AutomationDraft;
  readonly everyMinuteAvailable: boolean;
  readonly localExecutionAvailable: boolean;
  readonly projects: readonly AutomationProjectOption[];
  readonly runLocationLocked: boolean;
  readonly template: AutomationTemplateDefinition | null;
  readonly targetLocked: boolean;
  readonly providerSelection: {
    readonly providers: readonly ServerProvider[];
    /** Why the picker may withhold each provider it shows, per `ProviderModelPicker`. */
    readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
    readonly provider: ProviderKind;
    readonly model: string;
    readonly modelOptionsByProvider: Record<
      ProviderKind,
      ReadonlyArray<{ slug: string; name: string }>
    >;
    readonly orgProviderPolicy: CmmdOrgProviderPolicy | null;
    readonly statusText: string;
    readonly onChange: (provider: ProviderKind, model: string) => void;
  } | null;
  readonly providerStatusText: string;
  readonly onDraftChange: (draft: AutomationDraft) => void;
}

const FIELD_LABEL_CLASS = "mb-1.5 block text-xs font-medium text-muted-foreground";
const NATIVE_CONTROL_CLASS =
  "min-h-touch w-full rounded-md border border-input bg-background px-3 text-no-zoom text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/24 md:h-8 md:min-h-0 md:text-sm";

function updateDraft(draft: AutomationDraft, patch: Partial<AutomationDraft>): AutomationDraft {
  return { ...draft, ...patch };
}

export function CreateAutomationEditor(props: CreateAutomationEditorProps) {
  const showTime = props.draft.scheduleKind === "daily" || props.draft.scheduleKind === "weekly";
  const showDay = props.draft.scheduleKind === "weekly";
  return (
    <div className="space-y-8">
      <section aria-labelledby="automation-basics-heading">
        <h2 id="automation-basics-heading" className="text-sm font-semibold">
          Basics
        </h2>
        <div className="mt-3 space-y-4">
          <label className="block">
            <span className={FIELD_LABEL_CLASS}>Name</span>
            <Input
              aria-label="Name"
              className={NO_ZOOM_FIELD_TEXT_CLASS}
              value={props.draft.name}
              onChange={(event) =>
                props.onDraftChange(updateDraft(props.draft, { name: event.target.value }))
              }
            />
          </label>
        </div>
      </section>

      <section aria-labelledby="automation-location-heading">
        <h2 id="automation-location-heading" className="text-sm font-semibold">
          Runs on
        </h2>
        <div
          className={cn("mt-3 grid gap-2", props.localExecutionAvailable ? "sm:grid-cols-2" : null)}
        >
          <AutomationRunLocationChoice
            active={props.draft.runLocation === "cloud"}
            icon={CloudIcon}
            label="Forge Cloud"
            description="Runs in an isolated Sprite, even when this app is closed."
            disabled={props.runLocationLocked}
            onClick={() => props.onDraftChange(updateDraft(props.draft, { runLocation: "cloud" }))}
          />
          {props.localExecutionAvailable ? (
            <AutomationRunLocationChoice
              active={props.draft.runLocation === "local"}
              icon={LaptopIcon}
              label="This device"
              description="Runs only while this Forge environment is available."
              disabled={props.runLocationLocked}
              onClick={() =>
                props.onDraftChange(updateDraft(props.draft, { runLocation: "local" }))
              }
            />
          ) : null}
        </div>
      </section>

      <section aria-labelledby="automation-target-heading">
        <h2 id="automation-target-heading" className="text-sm font-semibold">
          Target
        </h2>
        <label className="mt-3 block">
          <span className={FIELD_LABEL_CLASS}>Project</span>
          <select
            aria-label="Project"
            className={NATIVE_CONTROL_CLASS}
            disabled={props.targetLocked}
            value={props.draft.selectedProjectId}
            onChange={(event) =>
              props.onDraftChange(
                updateDraft(props.draft, {
                  selectedProjectId: event.target.value,
                  modelSelection: null,
                }),
              )
            }
          >
            <option value="" disabled={props.draft.runLocation === "cloud"}>
              {props.draft.runLocation === "cloud" ? "Select a project" : "No project"}
            </option>
            {props.projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.githubRepo ? `${project.name} · ${project.githubRepo}` : project.name}
              </option>
            ))}
          </select>
          <span className="mt-1.5 block text-sm text-muted-foreground md:text-xs">
            {props.targetLocked
              ? "The target stays fixed so existing run history remains accurate."
              : "Cloud runs use this project’s repository and credentials."}
          </span>
        </label>
      </section>

      <section aria-labelledby="automation-schedule-heading">
        <h2 id="automation-schedule-heading" className="text-sm font-semibold">
          Schedule
        </h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={FIELD_LABEL_CLASS}>Repeats</span>
            <select
              aria-label="Repeats"
              className={NATIVE_CONTROL_CLASS}
              value={props.draft.scheduleKind}
              onChange={(event) =>
                props.onDraftChange(
                  updateDraft(props.draft, {
                    preservedCronExpression: null,
                    preservedScheduleDescription: null,
                    scheduleKind: event.target.value as AutomationScheduleKind,
                  }),
                )
              }
            >
              {props.draft.scheduleKind === "custom" ? (
                <option value="custom">Current custom schedule</option>
              ) : null}
              <option value="manual">Manual</option>
              {props.everyMinuteAvailable || props.draft.scheduleKind === "every_minute" ? (
                <option value="every_minute">Every minute</option>
              ) : null}
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </label>
          {showTime ? (
            <label className="block">
              <span className={FIELD_LABEL_CLASS}>Time</span>
              <Input
                aria-label="Time"
                className={NO_ZOOM_FIELD_TEXT_CLASS}
                type="time"
                value={props.draft.scheduleTime}
                onChange={(event) =>
                  props.onDraftChange(
                    updateDraft(props.draft, { scheduleTime: event.target.value }),
                  )
                }
              />
            </label>
          ) : (
            <span />
          )}
          {showDay ? (
            <label className="block sm:col-start-2">
              <span className={FIELD_LABEL_CLASS}>Day</span>
              <select
                aria-label="Day"
                className={NATIVE_CONTROL_CLASS}
                value={props.draft.scheduleDayOfWeek}
                onChange={(event) =>
                  props.onDraftChange(
                    updateDraft(props.draft, { scheduleDayOfWeek: Number(event.target.value) }),
                  )
                }
              >
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
                  (day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ),
                )}
              </select>
            </label>
          ) : null}
        </div>
      </section>

      {props.draft.runLocation === "cloud" ? (
        props.providerSelection ? (
          <AutomationModelPickerField
            model={props.providerSelection.model}
            modelOptionsByProvider={props.providerSelection.modelOptionsByProvider}
            orgProviderPolicy={props.providerSelection.orgProviderPolicy}
            projectSelected={props.draft.selectedProjectId.length > 0}
            provider={props.providerSelection.provider}
            providers={props.providerSelection.providers}
            providerAvailability={props.providerSelection.providerAvailability}
            statusText={props.providerSelection.statusText}
            onChange={props.providerSelection.onChange}
          />
        ) : (
          <section aria-labelledby="automation-model-heading">
            <h2 id="automation-model-heading" className="text-sm font-semibold">
              Model
            </h2>
            <p className="mt-3 text-sm text-muted-foreground md:text-xs">
              {props.providerStatusText}
            </p>
          </section>
        )
      ) : null}

      <section aria-labelledby="automation-instructions-heading">
        <h2 id="automation-instructions-heading" className="text-sm font-semibold">
          Instructions
        </h2>
        <label className="mt-3 block">
          <span className="sr-only">Prompt</span>
          <Textarea
            aria-label="Prompt"
            className={cn(NO_ZOOM_FIELD_TEXT_CLASS, "min-h-44 resize-y")}
            placeholder="Describe what Forge should do"
            value={props.draft.prompt}
            onChange={(event) =>
              props.onDraftChange(updateDraft(props.draft, { prompt: event.target.value }))
            }
          />
        </label>
      </section>

      {props.draft.runLocation === "cloud" ? (
        <section aria-labelledby="automation-safety-heading">
          <h2 id="automation-safety-heading" className="text-sm font-semibold">
            Safety
          </h2>
          <label className="mt-3 flex min-h-touch items-center gap-3 border-y border-border/60 py-3">
            <input
              checked={props.draft.requirePrReview}
              className="size-4 accent-primary"
              type="checkbox"
              onChange={(event) =>
                props.onDraftChange(
                  updateDraft(props.draft, { requirePrReview: event.target.checked }),
                )
              }
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <ShieldCheckIcon className="size-4" />
                Review consequential actions
              </span>
              <span className="mt-0.5 block text-sm text-muted-foreground md:text-xs">
                Pause before merge, deploy, send, or delete.
              </span>
            </span>
          </label>
        </section>
      ) : null}
    </div>
  );
}
